import React from 'react';
import Footer from '../components/Footer';
import Nav from '../components/Nav';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Nav />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 flex-1">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-3 sm:mb-4 leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
          Terms of Service
        </h1>
        <p className="text-sm text-[#1a1a1a]/60 mb-10 sm:mb-12">Effective Date: January 1, 2026</p>
        
        <div className="prose prose-base sm:prose-lg max-w-none space-y-6 sm:space-y-8">
          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              1. Acceptance of Terms
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              By accessing or using Verilock ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              2. Service Description
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              Verilock is a compliance guidance platform designed for early-stage SaaS founders. We provide AI-powered compliance assessments, policy templates, compliance roadmaps, and shareable Trust Pages. Our service is intended to help founders establish a professional security posture and communicate their compliance practices to prospects.
            </p>
            <p className="text-[#1a1a1a]/80 leading-relaxed mt-4 font-semibold">
              Important: Verilock provides compliance guidance and educational resources, not legal advice. For legal compliance requirements, consult a qualified attorney.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              3. Subscription Terms
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              <strong>Pricing:</strong> Verilock is offered as a monthly subscription service for $39 per month.
            </p>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              <strong>Billing:</strong> Your subscription will automatically renew each month on the anniversary of your sign-up date. Payment is processed through Paddle.
            </p>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              <strong>Changes to Pricing:</strong> We reserve the right to modify our pricing with 30 days' notice. Existing subscribers will be notified via email before any price changes take effect.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              4. Cancellation and Refund Policy
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              <strong>Cancellation:</strong> You may cancel your subscription at any time from your account settings. Cancellations take effect at the end of the current billing period. You will retain access to the Service until the end of your paid period.
            </p>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              <strong>Refund Policy:</strong> You may request a full refund within 14 days of your initial purchase. After 14 days, no refunds will be issued. To request a refund, contact <a href="mailto:hello@verilock.ai" className="underline hover:no-underline">hello@verilock.ai</a> with your account email. Cancellations take effect at the end of the current billing period.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              5. User Account and Responsibilities
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#1a1a1a]/80">
              <li>Maintaining the confidentiality of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Providing accurate and complete information</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              6. Acceptable Use
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#1a1a1a]/80">
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use the Service to transmit viruses, malware, or harmful code</li>
              <li>Resell or redistribute the Service without written permission</li>
              <li>Scrape, copy, or duplicate content from the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              7. Intellectual Property
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              The Service, including all content, features, and functionality, is owned by Verilock and is protected by copyright, trademark, and other intellectual property laws. The compliance documents and roadmaps generated for you are licensed to you for your business use.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              8. Limitation of Liability
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              <strong>No Legal Advice:</strong> Verilock provides compliance guidance and templates, not legal advice. We are not a law firm and do not provide legal services. The information and documents provided through our Service are for educational and informational purposes only.
            </p>
            <p className="text-[#1a1a1a]/80 leading-relaxed mb-4">
              <strong>Disclaimer of Warranties:</strong> The Service is provided "as is" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
            </p>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              <strong>Limitation:</strong> To the maximum extent permitted by law, Verilock shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              9. Indemnification
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              You agree to indemnify and hold harmless Verilock from any claims, damages, losses, liabilities, and expenses (including attorneys' fees) arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              10. Termination
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              We reserve the right to suspend or terminate your access to the Service at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              11. Governing Law and Disputes
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              12. Changes to Terms
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Service. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-normal mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
              13. Contact Information
            </h2>
            <p className="text-[#1a1a1a]/80 leading-relaxed">
              For questions about these Terms, please contact us at:{' '}
              <a href="mailto:hello@verilock.ai" className="text-[#0a5c44] hover:underline">
                hello@verilock.ai
              </a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;
